import update from "immutability-helper";

import React from "react";
import { connect } from "react-redux";

import { selector } from "../";
import { Modal } from "../../components/index";
import { addGuestlistPlace, editGuestlistPlace, fetchGuestlist, removeGuestlistPlace, setCurrentEditedGuestlistPlace, setShowDeleteModal } from "../../redux/actionCreators/guestlist";
import { type IEntryType, type IRow } from "../index";
import { type ApiError } from "../model";
import { getTotalUsedPlaces } from "./helpers";
import { PhosphorFloppyButton, PhosphorPencilButton, PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";

interface IProps {
  getEntryTypes: () => IEntryType[]
  getUnusedEntryTypes: () => IEntryType[]
  row: IRow
  onPlaceDelete: () => void
}

interface IPropsFromState {
  guestlist: server.dto.LegacyGuestlist
  currentEditedRow: IRow
  errors: ApiError[]
  isLoading: boolean
}

interface IPropsFromDispatch {
  setCurrentEditedRow: (row: IRow) => void
  addPlace: (place: server.dto.LegacyGuestlistPlace) => any
  removePlace: (placeId: number) => any
  editPlace: (place: server.dto.LegacyGuestlistPlace) => any
  refreshGuestlist: (showId: number) => any
  showDeleteModal: () => void
}

interface IState {
  unsavedRow: IRow
  customEntryInputHasError: boolean
  typeSelectHasError: boolean
  showCantDeleteModal: boolean
}

const mapStateToProps = (state): IPropsFromState => ({
  currentEditedRow: selector.getCurrentEditedRow(state),
  errors: selector.getPlaceManagementErrors(state),
  guestlist: selector.getGuestlist(state),
  isLoading: selector.getPlaceManagementIsLoading(state)
});

const mapDispatchToProps = (dispatch): IPropsFromDispatch => ({
  addPlace: (place: server.dto.LegacyGuestlistPlace) => dispatch(addGuestlistPlace(place)),
  editPlace: (place: server.dto.LegacyGuestlistPlace) => dispatch(editGuestlistPlace(place)),
  refreshGuestlist: (showId: number) => dispatch(fetchGuestlist(showId)),
  removePlace: (placeId: number) => dispatch(removeGuestlistPlace(placeId)),
  setCurrentEditedRow: (row: IRow) => dispatch(setCurrentEditedGuestlistPlace(row)),
  showDeleteModal: () => dispatch(setShowDeleteModal(true))

});

class TypeSettingsRow extends React.Component<IPropsFromState & IPropsFromDispatch & IProps, IState> {
  readonly NEW_ROW_ID: number = -1;
  readonly MAX_PERSONS: number = serverReference.constants.guestlist.maxGuestsPerList;

  componentDidMount() {
    this.setState({ unsavedRow: this.props.row, typeSelectHasError: false, customEntryInputHasError: false, showCantDeleteModal: false });
  }

  private readonly buildEditRow = (): JSX.Element => (
    <div className="flex-column gap">
      <div className="flex gap">
        <div style={{ width: 90 }}>{this.getPersonsSelect()}</div>
        <div className="grow1">{this.getCustomEntryInputForEditing()}</div>
        <PhosphorFloppyButton
          onClick={this.onSaveClick}
          padding="large"
          layout="in-square"
          backgroundColor="light"
        />
      </div>
      {this.buildErrors()}
    </div>);

  private readonly buildNonEditRow = (): JSX.Element => {
    const { row } = this.props;
    return (
      <div className="flex-column ">
        <div className="flex gap-small">
          <div style={{ width: 90 }}>{this.getUsedAndTotalSpotsLabel(row.place)}</div>
          <div className="grow1">{row.entryType.name}</div>
          <PhosphorTrashButton
            layout="in-square"
            onClick={this.onTrashClick} />
          <PhosphorPencilButton
            layout="in-square"
            onClick={this.onEditClick} />
        </div>
      </div>);
  };

  private readonly buildPlaceCreation = (): JSX.Element => (
    <div className="flex-column gap">
      <div className="flex gap">
        <div>{this.getPersonsSelect()}</div>
        <div className="grow1 flex">{this.getEntryTypesSelect()} {this.getCustomEntryInputForCreation()}</div>
        <PhosphorFloppyButton
          onClick={this.onAddClick}
          layout="in-square"
          padding="large"
          iconSize="large"
          backgroundColor="light"
        />
      </div>
      {this.buildErrors()}
    </div>);

  private readonly buildErrors = (): JSX.Element => {
    if (this.hasErrors()) {
      return (<div>
        {this.props.errors.map((error) => (<p key={error.code} style={{ color: "red" }}>{error.message}</p>))}
      </div>);
    }

    return null;
  };

  private readonly isBeingEdited = (): boolean => this.props.currentEditedRow != null && this.props.currentEditedRow.id === this.props.row.id;

  private readonly isBeingCreated = (): boolean => this.isBeingEdited() && this.props.row.id === this.NEW_ROW_ID;

  private readonly hasErrors = (): boolean => this.props.errors.length !== 0;

  private readonly getPersonsSelect = (): JSX.Element => {
    let minimumPeople: number;
    if (this.props.row?.place == null) {
      minimumPeople = 0;
    } else {
      minimumPeople = Math.min(getTotalUsedPlaces(this.props.row.place), this.MAX_PERSONS);

      // Minimum people is now 1-indexed. So 1 has to be subtracted to make it zero-indexed
      // Because later in the code, the code assumes it's a zero indexed value
      // However, if the value is already zero, it should stay zero
      minimumPeople = Math.max(minimumPeople - 1, 0);
    }

    // Eg. if there are 5 people on the list, then the list should start at "5 persons"
    const options: JSX.Element[] = [];
    for (let i = minimumPeople + 1; i <= this.MAX_PERSONS; i++) {
      const message = this.getTotalSpotsLabel(i);
      // From 1 to 20, keep all entries, from 20 to 50, only keep every 5th entry, from 50 onwards keep every 10th entry
      // This is to prevent the dropdown from becoming too large
      if ((i <= 20) || (i <= 50 && i % 5 === 0) || (i > 50 && i % 10 === 0)) {
        options.push(<option value={i} key={i}>{message}</option>);
      }
    }

    return (
      <select defaultValue={this.state.unsavedRow.persons.toString()} onChange={this.onPersonChange}>
        {options}
      </select>
    );
  };

  private readonly getUsedAndTotalSpotsLabel = (place: server.dto.LegacyGuestlistPlace): string => RESX.Guestlist.UsedSpots
    .replace("{0}", place.usedPlaces.toString())
    .replace("{1}", place.availableSpots.toString());

  private readonly getTotalSpotsLabel = (totalCapacity: number): string => RESX.Guestlist.PlaceCapacity
    .replace("{0}", totalCapacity.toString());

  private readonly getUsedSpotsLabel = (usedSpots: number): string => RESX.Guestlist.PlaceCapacityUsage
    .replace("{0}", usedSpots.toString());

  private readonly onPersonChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newAmountOfPersons = +event.currentTarget.value;
    const newState = update(this.state, {
      unsavedRow: {
        persons: { $set: newAmountOfPersons }
      }
    });

    this.setState(newState);
  };

  private readonly onEntryTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newTypeId = +event.currentTarget.value;
    const newState = update(this.state, {
      customEntryInputHasError: { $set: false },
      typeSelectHasError: { $set: false },
      unsavedRow: {
        entryType: {
          type: { $set: newTypeId }
        }
      }
    });
    this.setState(newState);
  };

  private readonly getEntryTypesSelect = (): JSX.Element => {
    const entryTypes: IEntryType[] = this.props.getUnusedEntryTypes();
    const errorStyle = this.state.typeSelectHasError ? { borderColor: "red" } : {};
    return (<select className="grow1" style={errorStyle} onChange={this.onEntryTypeChange}>
      <option value={-1} key={-1}></option>
      {entryTypes.map((v) => (<option value={v.type} key={v.type}>{v.name}</option>))}
    </select>);
  };

  private readonly onCustomEntryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    const newState = update(this.state, {
      unsavedRow: {
        entryType: {
          name: { $set: newValue }
        }
      }
    });

    this.setState(newState);
  };

  private readonly isOtherType = (): boolean => this.state.unsavedRow.entryType.type === 255;

  private readonly getCustomEntryInputForCreation = (): JSX.Element => {
    if (this.isOtherType()) {
      return this.getCustomEntryTypeInput();
    }

    return null;
  };

  private readonly getCustomEntryInputForEditing = (): JSX.Element => {
    if (this.isOtherType()) {
      return this.getCustomEntryTypeInput();
    }

    return (<div>{this.state.unsavedRow.entryType.name}</div>);
  };

  private readonly getCustomEntryTypeInput = (): JSX.Element => {
    const style = this.state.customEntryInputHasError ? { borderColor: "red", marginLeft: 5 } : { marginLeft: 5 };

    return (<input className="grow1" placeholder={"\"Special access\""} style={style} value={this.state.unsavedRow.entryType.name} onChange={this.onCustomEntryChange} type="text" />);
  };

  private readonly onSaveClick = () => {
    if (this.props.isLoading) {
      return;
    }
    if (!this.isCustomEntryTypeNameValid()) {
      this.setState({ customEntryInputHasError: true });
      return;
    }

    const { unsavedRow } = this.state;
    const newPlace: server.dto.LegacyGuestlistPlace = update(unsavedRow.place, {
      availableSpots: { $set: unsavedRow.persons },
      entryType: {
        name: {
          $set: unsavedRow.entryType.name
        }
      }
    });
    this.props.editPlace(newPlace)
      .then(this.handleErrorsOrStopEditing);
  };

  private readonly onTrashClick = () => {
    if (this.props.isLoading) {
      return;
    }

    const placeId = this.props.row.place.guestlistPlaceId;
    if (this.props.row.place.guests.length > 0) {
      this.props.showDeleteModal();
    } else {
      this.props.removePlace(placeId)
        .then(() => this.props.refreshGuestlist(this.props.guestlist.showId))
        .then(() => { this.props.onPlaceDelete(); });
    }
  };

  private readonly onEditClick = (): void => {
    this.props.setCurrentEditedRow(this.props.row);
    this.setState({ unsavedRow: this.props.row, customEntryInputHasError: false });
  };

  private readonly onAddClick = () => {
    if (this.props.isLoading) {
      return;
    }
    if (!this.isEntryTypeValid()) {
      this.setState({ typeSelectHasError: true });
      return;
    }
    if (!this.isCustomEntryTypeNameValid()) {
      this.setState({ customEntryInputHasError: true });
      return;
    }
    const newPlace: server.dto.LegacyGuestlistPlace = this.createPlaceFromRow();
    this.props.addPlace(newPlace)
      .then(this.handleErrorsOrStopEditing);
  };

  private readonly handleErrorsOrStopEditing = (): void => {
    if (this.hasErrors()) {
      return;
    }
    this.stopEditing();
    this.props.refreshGuestlist(this.props.guestlist.showId);
  };

  private readonly stopEditing = (): void => {
    if (this.isBeingEdited()) {
      this.props.setCurrentEditedRow(null);
    }
  };

  private readonly createPlaceFromRow = (): server.dto.LegacyGuestlistPlace => {
    const { unsavedRow } = this.state;
    return {
      availableSpots: unsavedRow.persons,
      emptyPlaces: unsavedRow.persons,
      entryType: unsavedRow.entryType,
      guestlistId: this.props.guestlist.guestlistId,
      guestlistPlaceId: 0,
      guests: null,
      usedPlaces: 0
    };
  };

  private readonly isEntryTypeValid = (): boolean => this.state.unsavedRow.entryType.type >= 0;

  private readonly isCustomEntryTypeNameValid = (): boolean => {
    if (this.isOtherType()) {
      return this.state.unsavedRow.entryType.name.trim() !== "";
    }

    return true;
  };

  public render(): JSX.Element {
    if (this.state == null) {
      return null;
    }

    if (this.state.showCantDeleteModal) {
      return <Modal handleClose={() => { this.setState({ showCantDeleteModal: false }); }}>
        {RESX.GuestlistErrors.ListContainsGuests}
      </Modal>;
    }

    if (this.isBeingCreated()) {
      return this.buildPlaceCreation();
    }
    if (this.isBeingEdited()) {
      return this.buildEditRow();
    }

    return this.buildNonEditRow();
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TypeSettingsRow);
